import type { DashboardWidgetType } from '../components/dashboard/Dashboard.types';
import { getCookieCustom } from '../utils/cookies';
import { getStoragePrefix } from './useConfigStorage';

/**
 * Pre-namespacing key, shared by every user and database in this browser.
 * Read-only: each user of a shared browser migrates it once.
 */
export const LEGACY_DASHBOARD_WIDGETS_KEY = 'dashboard.widgets';

/**
 * Fired after a widget patches its own entry in the stored list, so the
 * dashboard in this tab adopts it. The native 'storage' event only reaches
 * other tabs (a popout's patch arrives at the dashboard tab that way).
 */
export const WIDGET_PATCH_EVENT = 'dashboard-widget-patch';

export type WidgetPatchDetail = {
	id: string;
	patch: Record<string, unknown>;
};

/** Resolved per call: the user and database change on login and db switch. */
export const dashboardWidgetsStorageKey = (): string =>
	`${getStoragePrefix()}_dashboard.widgets.${getCookieCustom('user_id') || 'anon'}`;

export const isDashboardWidgetsKey = (key: string | null): boolean =>
	key === dashboardWidgetsStorageKey() || key === LEGACY_DASHBOARD_WIDGETS_KEY;

/**
 * Set while the stored list holds edits the server has not received (a popout
 * patch with no dashboard open, or a tab closed inside the save debounce).
 * The next dashboard mount then keeps the local list and uploads it.
 */
const unsyncedMarkerKey = (storageKey = dashboardWidgetsStorageKey()): string =>
	`${storageKey}.unsynced`;

export const markWidgetsUnsynced = (): void => {
	try {
		localStorage.setItem(unsyncedMarkerKey(), '1');
	} catch {
		/* storage unavailable */
	}
};

/** Pass the cache key a save was made for; it defaults to the current one. */
export const clearWidgetsUnsynced = (storageKey?: string): void => {
	try {
		localStorage.removeItem(unsyncedMarkerKey(storageKey));
	} catch {
		/* storage unavailable */
	}
};

export const hasUnsyncedWidgets = (): boolean => {
	try {
		return localStorage.getItem(unsyncedMarkerKey()) !== null;
	} catch {
		return false;
	}
};

/** Filters null and other non-objects out of a corrupt stored list. */
export const isWidgetEntry = (w: unknown): w is DashboardWidgetType =>
	!!w && typeof w === 'object';

export const readStoredWidgets = (
	storageKey = dashboardWidgetsStorageKey(),
): DashboardWidgetType[] => {
	try {
		const raw =
			localStorage.getItem(storageKey) ??
			localStorage.getItem(LEGACY_DASHBOARD_WIDGETS_KEY);
		if (!raw) return [];
		const parsed: unknown = JSON.parse(raw);
		return Array.isArray(parsed) ? parsed.filter(isWidgetEntry) : [];
	} catch {
		return [];
	}
};

/**
 * Writes the namespaced key. The legacy key is left alone (see above).
 * Throws when storage is unavailable; callers decide how to degrade.
 */
export const writeStoredWidgets = (serialized: string): void => {
	localStorage.setItem(dashboardWidgetsStorageKey(), serialized);
};

export const patchStoredWidget = (
	id: string,
	patch: Record<string, unknown>,
): boolean => {
	const list = readStoredWidgets();
	if (!list.some((w) => w.id === id)) return false;
	const next = list.map((w) => (w.id === id ? { ...w, ...patch } : w));
	try {
		writeStoredWidgets(JSON.stringify(next));
	} catch {
		return false;
	}
	markWidgetsUnsynced();
	window.dispatchEvent(
		new CustomEvent<WidgetPatchDetail>(WIDGET_PATCH_EVENT, {
			detail: { id, patch },
		}),
	);
	return true;
};
